<template>
  <b-modal
    :active.sync="showModal"
    trap-focus
    aria-role="dialog"
    scroll="keep"
    :width="1000"
    aria-modal
    has-modal-card
    @close="closeModal"
  >
    <ValidationObserver ref="observer" v-slot="{ passes }">
      <form @submit.prevent="passes(save)">
        <div class="modal-card" style="width: 1000px; position: relative;">
          <!-- Full modal loading overlay -->
          <div v-if="loading" class="modal-loading-overlay">
            <div class="loading-content">
              <b-loading :is-full-page="false" :active="true" />
              <p class="loading-text">{{ loadingMessage }}</p>
            </div>
          </div>
          <header class="modal-card-head">
            <p class="modal-card-title">Add to Schedule</p>
          </header>
          <section class="modal-card-body" style="height: 600px">
            <ValidationProvider
              v-slot="{ errors }"
              name="Title"
              rules="required"
            >
              <b-field
                class="has-margin-top-15"
                label="Title"
                :type="{ 'is-danger': errors.length > 0 }"
                :message="errors[0]"
              >
                <b-autocomplete
                  v-model="media.title"
                  placeholder="Select a title"
                  :data="filteredTitles"
                  field="title"
                  expanded
                  :open-on-focus="true"
                  @select="
                    value => {
                      if (value && value.id) {
                        selectedTitle = value
                      }
                    }
                  "
                />
              </b-field>
            </ValidationProvider>
            <BaseSelect
              v-if="selectedTitle && selectedTitle.media_type == 'series'"
              v-model="media.episode_id"
              label="Episode"
              :rules="'required'"
            >
              <option
                v-for="episode in episodes"
                :key="episode.id"
                :value="episode.id"
              >
                {{ episodeStatusDot(episode) }}
                {{ episode.episode_number }}
                {{ episode.title ? `- ${episode.title}` : '' }}
              </option>
            </BaseSelect>
            <div class="columns is-multiline">
              <div class="column is-6">
                <BaseDateTimePicker
                  v-model="media.start_time"
                  label="Start Time"
                  :rules="'required'"
                  @input="updateEndTime(media)"
                >
                </BaseDateTimePicker>
              </div>
              <div class="column is-6">
                <BaseDateTimePicker
                  :key="'end-' + (media.end_time ? media.end_time.getTime() : 'empty')"
                  v-model="media.end_time"
                  label="End Time"
                  :rules="'required'"
                  :min-datetime="media.start_time"
                  :placeholder="selectedTitle && selectedTitle.id ? '' : 'Select a title first'"
                >
                </BaseDateTimePicker>
              </div>
              <div v-if="!editingitem" class="column is-12">
                <b-collapse :open="showSlotPicker" animation="slide">
                  <template #trigger="props">
                    <a @click.prevent="showSlotPicker = !showSlotPicker">
                      <b-icon
                        :icon="props.open ? 'menu-down' : 'menu-right'"
                      ></b-icon>
                      {{ props.open ? 'Hide' : 'Add' }} Additional Slots
                    </a>
                  </template>

                  <div v-if="selectedTitle" class="box has-margin-top-15">
                    <SlotPickerCalendar
                      :channel-id="channelId"
                      :duration="getMediaDuration()"
                      :initial-start-date="media.start_time"
                      :episode-count="remainingEpisodeCount"
                      @slots-changed="onSlotsChanged"
                    />
                  </div>
                </b-collapse>
              </div>
            </div>
            <div
              v-if="media.additional && media.additional.length > 0 && !editingitem"
              class="box has-background-light has-margin-top-10"
            >
              <p class="has-text-weight-bold">
                Additional Slots Added ({{ media.additional.length }}):
              </p>
              <div class="columns is-multiline has-margin-top-10">
                <div
                  v-for="(item, index) in media.additional"
                  :key="index"
                  class="column is-12"
                >
                  <b-tag
                    type="is-primary"
                    closable
                    @close="removeAdditional(index)"
                  >
                    {{ formatSlotDisplay(item) }}
                  </b-tag>
                </div>
              </div>
            </div>
            <b-field v-if="!editingitem" class="has-margin-top-10">
              <b-checkbox v-model="repeat">Repeat?</b-checkbox>
            </b-field>
            <BaseSelect
              v-if="repeat"
              v-model="frequency"
              label="Frequency"
              :rules="'required'"
            >
              <option value="daily">Same time Daily</option>
              <option value="daily-selected">
                Same time Daily (Selected Days only)
              </option>
              <option value="weekly">Same day and time Weekly</option>
              <option value="weekly-selected">
                Same time Weekly (Selected Days - new ep each week)
              </option>
            </BaseSelect>
            <BaseSelect
              v-if="
                repeat && selectedTitle && selectedTitle.media_type == 'series'
              "
              v-model="episoderepeat"
              label="Repeat episodes"
              :rules="'required'"
            >
              <option value="same">Same Episode</option>
              <option value="following">Following Episodes</option>
              <option value="alternating">Alternating Episodes (skip 1)</option>
            </BaseSelect>
            <BaseInput
              v-if="
                repeat &&
                  ((selectedTitle &&
                    selectedTitle.media_type === 'series' &&
                    episoderepeat === 'same') ||
                    (selectedTitle && selectedTitle.media_type === 'movie'))
              "
              v-model="repeatnumber"
              label="Number of Repeats"
              type="number"
              rules="required"
            />
            <b-field
              v-if="
                repeat &&
                selectedTitle &&
                selectedTitle.media_type === 'series' &&
                (episoderepeat === 'following' || episoderepeat === 'alternating')
              "
              label="Episodes Per Day"
              class="has-margin-top-10"
            >
              <b-numberinput
                v-model="episodesPerDay"
                :min="1"
                :max="10"
                controls-position="compact"
              ></b-numberinput>
              <p class="help">
                Back-to-back scheduling: Set to 2+ to schedule multiple sequential episodes per day.
                E.g., 2 = EP1 &amp; EP2 on Mon, EP3 &amp; EP4 on Tue.
              </p>
            </b-field>
            <div v-if="frequency === 'daily-selected' || frequency === 'weekly-selected'">
              <!-- Auto-detected days display -->
              <div v-if="daysAutoDetected && repeatdays.length > 0" class="has-margin-top-10">
                <b-field label="Schedule Days">
                  <div class="auto-detected-days">
                    <b-icon icon="check-circle" type="is-success" size="is-small"></b-icon>
                    <span class="has-text-weight-semibold">Auto-detected from slots: </span>
                    <span class="day-tags">
                      <b-tag v-for="day in repeatdays" :key="day" type="is-info" class="has-margin-right-5">
                        {{ getDayName(day) }}
                      </b-tag>
                    </span>
                    <a class="has-margin-left-10" @click.prevent="daysAutoDetected = false">
                      <b-icon icon="pencil" size="is-small"></b-icon> Edit
                    </a>
                  </div>
                </b-field>
                <!-- Explain the difference for weekly-selected -->
                <p v-if="frequency === 'weekly-selected'" class="help has-text-info">
                  Weekly mode: Same episode plays on ALL selected days within each week. New episode starts next week.
                </p>
              </div>
              <!-- Manual day selection -->
              <b-field
                v-else
                class="has-margin-top-10"
                label="Repeat Certain Days only?"
              >
                <div>
                  <b-checkbox v-model="repeatdays" :native-value="1"
                    >Mon</b-checkbox
                  >
                  <b-checkbox v-model="repeatdays" :native-value="2"
                    >Tue</b-checkbox
                  >
                  <b-checkbox v-model="repeatdays" :native-value="3"
                    >Wed</b-checkbox
                  >
                  <b-checkbox v-model="repeatdays" :native-value="4"
                    >Thu</b-checkbox
                  >
                  <b-checkbox v-model="repeatdays" :native-value="5"
                    >Fri</b-checkbox
                  >
                  <b-checkbox v-model="repeatdays" :native-value="6"
                    >Sat</b-checkbox
                  >
                  <b-checkbox v-model="repeatdays" :native-value="0"
                    >Sun</b-checkbox
                  >
                </div>
              </b-field>
              <div v-if="!daysAutoDetected">
                <a class="has-margin-right-10" @click.prevent="selectAllDays"
                  >Select All</a
                >
                <a @click.prevent="unselectAllDays">Unselect All</a>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot" style="display: block">
            <button class="button" type="button" @click="closeModal">
              Cancel
            </button>
            <button
              class="button is-primary"
              :loading="loading"
              @click.prevent="passes(save)"
            >
              {{ editingitem ? 'Update' : 'Add' }}
            </button>
            <button
              v-if="editingitem"
              class="button is-info is-pulled-right"
              :loading="loading"
              @click.prevent="createCopy"
            >
              Copy
            </button>
            <button
              v-if="editingitem && editingitem.repeats_id"
              class="button is-info is-pulled-right"
              :loading="loading"
              @click.prevent="editSequence"
            >
              Edit Sequence
            </button>
            <button
              v-if="editingitem"
              class="button is-info is-pulled-right has-margin-right-5"
              :loading="loading"
              @click.prevent="confirmDelete"
            >
              Delete
            </button>
          </footer>
        </div>
      </form>
    </ValidationObserver>
    <EditSequenceModal
      v-if="item"
      :repeats-id="item.repeats_id"
      :channel-id="channelId"
      :show="showEditSequenceModal"
      @close-modal="showEditSequenceModal = false"
      @refresh="
        $emit('refresh')
        showModal = false
        $emit('close-modal')
      "
    >
    </EditSequenceModal>

    <!-- Schedule Clash Dialog -->
    <b-modal :active.sync="showClashModal" has-modal-card trap-focus aria-role="dialog" aria-modal :can-cancel="['escape']">
      <div class="modal-card" style="max-width: 600px; margin: 0 auto;">
        <header class="modal-card-head" style="background-color: #ffdd57;">
          <p class="modal-card-title">
            <b-icon icon="alert-circle" type="is-warning" style="margin-right: 8px;" />
            Schedule Clash Detected
          </p>
        </header>
        <section class="modal-card-body">
          <div v-for="(clash, index) in clashDialogData.clashes" :key="index" style="margin-bottom: 15px;">
            <div><strong>New Item {{ index + 1 }}:</strong></div>
            <div>{{ formatClashTime(clash.newItem.start_time) }} - {{ formatClashTimeOnly(clash.newItem.end_time) }}</div>
            <div style="margin-top: 8px;"><strong>Conflicts with:</strong></div>
            <div v-for="(conflict, ci) in clash.conflicts" :key="ci" style="margin-left: 15px; color: #d9534f;">
              &bull; {{ conflict.title }}<br/>
              &nbsp;&nbsp;{{ formatClashTime(conflict.start_time) }} - {{ formatClashTimeOnly(conflict.end_time) }}
            </div>
          </div>
          <div v-if="clashDialogData.firstClashIndex > 0" style="margin-top: 15px; padding: 10px; background: #f5f5f5; border-radius: 4px;">
            <strong>{{ clashDialogData.firstClashIndex }}</strong> of <strong>{{ clashDialogData.totalItems }}</strong> items can be scheduled before the first clash.
          </div>
          <div v-else-if="clashDialogData.firstClashIndex === 0" style="margin-top: 15px; padding: 10px; background: #fff3cd; border-radius: 4px;">
            The first item already has a clash. No items can be scheduled before it.
          </div>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end; gap: 8px;">
          <b-button @click="showClashModal = false">Cancel</b-button>
          <b-button
            v-if="clashDialogData.firstClashIndex > 0"
            type="is-info"
            @click="scheduleUpToClash"
          >
            Schedule {{ clashDialogData.firstClashIndex }} item{{ clashDialogData.firstClashIndex > 1 ? 's' : '' }}
          </b-button>
          <b-button type="is-warning" @click="overwriteAndSave(clashDialogData.clashes)">
            Overwrite
          </b-button>
        </footer>
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
import { sortBy } from 'lodash'
import BaseDateTimePicker from '@/components/BaseDateTimePicker'
import BaseSelect from '@/components/BaseSelect'
import EditSequenceModal from '@/components/EditSequenceModal'
import SlotPickerCalendar from '@/components/SlotPickerCalendar'
import MediaChannelApi from '@/api/mediaChannels'
import EpisodesApi from '@/api/episodes'
import { episodeStatusDot } from '@/utils/episodeStatus'
import ScheduleApi from '@/api/schedules'
import Filters from '@/mixins/filters'
import Utils from '@/mixins/utils'
import addMinutes from 'date-fns/addMinutes'
import format from 'date-fns/format'

export default {
  name: 'AddToScheduleModal',
  components: {
    BaseDateTimePicker,
    BaseSelect,
    EditSequenceModal,
    SlotPickerCalendar
  },
  mixins: [Filters, Utils],
  props: {
    show: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      default: null
    },
    channelId: {
      type: Number,
      default: null
    },
    start: {
      type: Date,
      default: null
    }
  },
  data() {
    // When editing an existing item, use its start/end times
    // When adding new, use the provided start time and leave end_time null until title is selected
    const isEditing = this.item && this.item.id
    const startTime = isEditing ? this.item.start : (this.start || new Date())
    const endTime = isEditing ? this.item.end : null

    return {
      showEditSequenceModal: false,
      loading: false,
      loadingMessage: 'Saving schedule...',
      initialized: false,
      showModal: this.show,
      selectedTitle: {},
      repeat: false,
      frequency: '',
      episoderepeat: '',
      editingitem: this.item,
      media: {
        start_time: startTime,
        end_time: endTime, // Populated for editing, null for new items until title is selected
        episode_id: isEditing ? this.item.episode_id : undefined,
        additional: []
      },
      episodes: [],
      repeatdays: [],
      repeatnumber: '',
      episodesPerDay: 1,
      daysAutoDetected: false,
      showSlotPicker: false,
      showClashModal: false,
      clashDialogData: { clashes: [], firstClashIndex: -1, totalItems: 0 }
    }
  },
  computed: {
    filteredTitles() {
      if (this.media && this.media.title) {
        let filter = this.media.title.toLowerCase()
        return this.titles.filter(item =>
          item.title.toLowerCase().includes(filter)
        )
      }
    },
    /**
     * Calculate remaining episodes to schedule starting from selected episode
     * For movies, returns 1. For series, returns count from selected episode to last.
     */
    remainingEpisodeCount() {
      if (!this.selectedTitle) return 1
      if (this.selectedTitle.media_type === 'movie') return 1
      if (!this.media.episode_id || this.episodes.length === 0) return 1

      // Find the selected episode index
      const selectedEpisode = this.episodes.find(ep => ep.id === this.media.episode_id)
      if (!selectedEpisode) return 1

      // Count episodes from selected to end
      const selectedEpisodeNumber = selectedEpisode.episode_number
      const remainingEpisodes = this.episodes.filter(
        ep => ep.episode_number >= selectedEpisodeNumber
      )
      return remainingEpisodes.length || 1
    }
  },
  watch: {
    show() {
      this.showModal = this.show
    },
    async selectedTitle(newVal, oldVal) {
      if (this.selectedTitle && this.selectedTitle.media_type === 'series') {
        let episodes = await EpisodesApi.getAllByMediaId(this.selectedTitle.id)
        episodes.forEach(
          episode =>
            (episode.episode_number = parseInt(episode.episode_number, 10))
        )
        this.episodes = sortBy(episodes, 'episode_number')
        if (!this.editingitem) {
          this.media.episode_id = undefined
        }
      }
      if (
        this.selectedTitle &&
        this.selectedTitle.mediadata &&
        this.media.start_time
      ) {
        let mediaData = JSON.parse(this.selectedTitle.mediadata)
        if (mediaData && mediaData.mediaLength) {
          this.media.end_time = addMinutes(this.media.start_time,
            parseInt(mediaData.mediaLength, 10)
          )
          if (this.media.additional && this.media.additional.length > 0) {
            this.media.additional.forEach(item => {
              item.end_time = addMinutes(item.start_time,
                parseInt(mediaData.mediaLength, 10)
              )
            })
          }
          this.$forceUpdate()
        }
      }
    },
    'media.episode_id': function(newVal, oldVal) {
      this.setEpisode()
    },
    'media.start_time': function(newVal, oldVal) {
      if (
        this.selectedTitle &&
        this.selectedTitle.media_type === 'movie' &&
        this.selectedTitle.mediadata
      ) {
        let mediaData = JSON.parse(this.selectedTitle.mediadata)
        if (mediaData && mediaData.mediaLength) {
          this.media.end_time = addMinutes(this.media.start_time,
            parseInt(mediaData.mediaLength, 10)
          )
        }
      } else if (
        this.media.episode_id &&
        this.selectedTitle.media_type === 'series'
      ) {
        let episode = this.episodes.find(
          item => item.id === this.media.episode_id
        )
        if (episode && episode.episodedata) {
          let episodedata = JSON.parse(episode.episodedata)
          if (episodedata.episodeLength) {
            this.media.end_time = addMinutes(this.media.start_time,
              parseInt(episodedata.episodeLength, 10)
            )
          }
        }
      }
    }
  },
  async created() {
    // Ensure media.additional is always initialized as an array
    if (!this.media.additional) {
      this.$set(this.media, 'additional', [])
    }

    let allTitles = await MediaChannelApi.getByChannelId(this.channelId)
    // Week view schedule handles series and movies - promos/TVC/filler/GFX go in day view
    let titles = allTitles.filter(item => item.media_type === 'series' || item.media_type === 'movies')
    titles.forEach(item => {
      let mediadata = null
      if (item.mediadata) {
        mediadata = JSON.parse(item.mediadata)
      }
      item.title =
        item.title +
        (item.media_type === 'series' && mediadata && mediadata.seriesNo
          ? ` S${mediadata.seriesNo}`
          : '') +
        ` - ${this.$options.filters.capitalize(item.media_type)}`
    })
    this.titles = titles
    if (this.editingitem) {
      const found = this.titles.find(
        item => item.id === this.editingitem.media_id
      )
      if (found) {
        this.selectedTitle = found
        this.media = {
          id: this.editingitem.id,
          // Backend already converted to channel timezone
          start_time: this.editingitem.start,
          end_time: this.editingitem.end,
          title: found.title,
          episode_id: this.editingitem.episode_id
        }
      }
    }
    this.initialized = true
  },
  methods: {
    episodeStatusDot,
    editSequence() {
      this.showEditSequenceModal = true
    },
    updateEndTime(item) {
      if (
        this.selectedTitle &&
        this.selectedTitle.media_type === 'movie' &&
        this.selectedTitle.mediadata
      ) {
        let mediaData = JSON.parse(this.selectedTitle.mediadata)
        if (mediaData && mediaData.mediaLength) {
          item.end_time = addMinutes(item.start_time,
            parseInt(mediaData.mediaLength, 10)
          )
        }
      } else if (
        this.media.episode_id &&
        this.selectedTitle.media_type === 'series'
      ) {
        let episode = this.episodes.find(
          item => item.id === this.media.episode_id
        )
        if (episode && episode.episodedata) {
          let episodedata = JSON.parse(episode.episodedata)
          if (episodedata.episodeLength) {
            item.end_time = addMinutes(item.start_time,
              parseInt(episodedata.episodeLength, 10)
            )
          }
        }
      }
    },
    removeAdditional(index) {
      this.media.additional.splice(index, 1)
    },
    async createCopy() {
      try {
        this.loading = true
        this.editingitem.channel_id = this.channelId
        this.editingitem.media_id = this.selectedTitle.id
        this.editingitem.start_time = this.editingitem.start
        this.editingitem.end_time = this.editingitem.end
        this.editingitem.copy = true
        delete this.editingitem.id
        await ScheduleApi.add(this.editingitem)
        this.$buefy.toast.open({
          message: 'Schedule updated successfully!',
          position: 'is-top',
          type: 'is-success'
        })
        this.loading = false
        this.$emit('refresh')
        this.closeModal()
      } catch (err) {
        console.log(err)
        this.$buefy.toast.open({
          message: 'Error saving schedule',
          position: 'is-top',
          type: 'is-danger'
        })
        this.loading = false
      }
    },
    confirmDelete() {
      this.$buefy.dialog.confirm({
        message: 'Are you sure you want to delete this item?',
        onConfirm: async () => {
          this.loading = true
          await ScheduleApi.remove(this.media)
          this.$buefy.toast.open({
            message: 'Schedule item removed successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          this.loading = false
          this.$emit('refresh')
          this.closeModal()
        }
      })
    },
    setEpisode() {
      if (this.media.episode_id) {
        let episode = this.episodes.find(
          item => item.id === this.media.episode_id
        )
        if (episode && episode.episodedata) {
          let episodedata = JSON.parse(episode.episodedata)
          if (episodedata.episodeLength) {
            this.media.end_time = addMinutes(this.media.start_time,
              parseInt(episodedata.episodeLength, 10)
            )
            if (this.media.additional && this.media.additional.length > 0) {
              this.media.additional.forEach(item => {
                item.end_time = addMinutes(item.start_time,
                  parseInt(episodedata.episodeLength, 10)
                )
              })
            }
          }
        }
      }
    },
    async save() {
      try {
        this.loading = true
        this.media.channel_id = this.channelId
        this.media.media_id = this.selectedTitle.id
        // Clear any stale clash resolution flags from previous attempts
        delete this.media.copy
        delete this.media.scheduleUpToClash

        if (this.editingitem) {
          this.loadingMessage = 'Updating schedule...'
          await ScheduleApi.update(this.media)
          this.$buefy.toast.open({
            message: 'Schedule updated successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          this.loading = false
          this.$emit('refresh')
          this.closeModal()
          return
        } else {
          this.media.repeat = this.repeat
          this.media.frequency = this.frequency
          this.media.episoderepeat = this.episoderepeat
          this.media.repeatdays = this.repeatdays
          this.media.repeatnumber = this.repeatnumber
            ? parseInt(this.repeatnumber, 10)
            : undefined
          this.media.episodes_per_day = this.episodesPerDay
            ? parseInt(this.episodesPerDay, 10)
            : 1

          // Show informative loading message for large schedules
          const totalSlots = 1 + (this.media.additional ? this.media.additional.length : 0)
          let episodeCount = 1
          if (this.episoderepeat === 'following') {
            episodeCount = this.remainingEpisodeCount
          } else if (this.episoderepeat === 'alternating') {
            // Alternating mode schedules every other episode
            episodeCount = Math.ceil(this.remainingEpisodeCount / 2)
          }
          const totalItems = totalSlots * episodeCount
          if (totalItems > 10) {
            this.loadingMessage = `Creating ${totalItems} schedule entries with segments...`
          } else {
            this.loadingMessage = 'Creating schedule...'
          }

          await ScheduleApi.add(this.media)
          this.$buefy.toast.open({
            message: 'Schedule updated successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          this.loading = false
          this.$emit('refresh')
          this.closeModal()
        }
      } catch (err) {
        console.log(err)
        if (err && err.status === 409 && err.data && err.data.clashes) {
          // Show detailed clash information with options: overwrite, schedule up to clash, cancel
          this.showClashDialog(err.data.clashes, err.data.firstClashIndex, err.data.totalItems)
        } else if (!err || err.status === undefined || err.status === 504 || err.status === 0) {
          // Proxy timeout / no response (e.g. large sequence on a dense channel).
          // The server may well have committed the write, so do NOT report failure
          // (re-submitting would create duplicates). Refresh so staff can verify.
          this.$buefy.toast.open({
            message:
              'This is taking longer than expected to save. Refreshing to verify — please do NOT re-submit; check the grid before trying again.',
            position: 'is-top',
            type: 'is-warning',
            duration: 8000
          })
          this.$emit('refresh')
          this.closeModal()
        } else {
          this.$buefy.toast.open({
            message:
              err.status === 409
                ? 'There is a schedule clash with another program with the same timeslot. Please update the time to a different available timeslot.'
                : 'Error saving schedule',
            position: 'is-top',
            type: 'is-danger'
          })
        }

        this.loading = false
      }
    },
    closeModal() {
      this.showModal = false
      this.$emit('close-modal')
    },
    selectAllDays() {
      this.repeatdays = [0, 1, 2, 3, 4, 5, 6]
    },
    unselectAllDays() {
      this.repeatdays = []
    },
    getDayName(dayNum) {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      return days[dayNum] || ''
    },
    onSlotsChanged(slots) {
      // Slots come from SlotPickerCalendar
      // Filter out slots that match the primary start_time to avoid duplicates
      // The backend adds both the primary item AND additional items, so we must
      // exclude the primary slot from additional if it's the same time
      const primaryStart = this.media.start_time ? this.media.start_time.getTime() : null

      this.media.additional = slots
        .filter(slot => {
          // Exclude slot if it matches the primary start_time
          if (!primaryStart) return true
          const slotStart = slot.start_time ? slot.start_time.getTime() : null
          return slotStart !== primaryStart
        })
        .map(slot => ({
          start_time: slot.start_time,
          end_time: slot.end_time
        }))

      // AUTO-DETECT: Extract unique days from all slots (including primary)
      if (slots.length > 0 || this.media.start_time) {
        const allSlots = this.media.start_time
          ? [{ start_time: this.media.start_time }, ...slots]
          : slots

        const uniqueDays = [...new Set(
          allSlots
            .filter(slot => slot.start_time)
            .map(slot => {
              const date = new Date(slot.start_time)
              return date.getDay() // 0=Sun, 1=Mon, ..., 6=Sat
            })
        )].sort((a, b) => {
          // Sort Mon-Sun (1,2,3,4,5,6,0)
          const order = [1, 2, 3, 4, 5, 6, 0]
          return order.indexOf(a) - order.indexOf(b)
        })

        if (uniqueDays.length > 0) {
          // Auto-set frequency and days from slots
          this.frequency = 'daily-selected'
          this.repeatdays = uniqueDays
          this.daysAutoDetected = true
        }
      }
    },
    getMediaDuration() {
      // Calculate duration in minutes from selected media
      if (this.selectedTitle && this.selectedTitle.mediadata) {
        const mediaData = JSON.parse(this.selectedTitle.mediadata)
        return mediaData.mediaLength || 60
      }
      if (this.media.episode_id && this.episodes.length > 0) {
        const episode = this.episodes.find(
          ep => ep.id === this.media.episode_id
        )
        if (episode && episode.episodedata) {
          const episodeData = JSON.parse(episode.episodedata)
          return episodeData.episodeLength || 60
        }
      }
      return 60 // Default 60 minutes
    },
    formatSlotDisplay(slot) {
      return `${format(slot.start_time, 'EEE MMM d')} @ ${format(
        slot.start_time,
        'HH:mm'
      )}`
    },
    showClashDialog(clashes, firstClashIndex, totalItems) {
      this.clashDialogData = {
        clashes: clashes || [],
        firstClashIndex: firstClashIndex != null ? firstClashIndex : -1,
        totalItems: totalItems || 0
      }
      this.showClashModal = true
    },
    formatClashTime(dateStr) {
      return format(new Date(dateStr), 'yyyy-MM-dd HH:mm')
    },
    formatClashTimeOnly(dateStr) {
      return format(new Date(dateStr), 'HH:mm')
    },
    async scheduleUpToClash() {
      try {
        this.showClashModal = false
        this.loading = true
        this.loadingMessage = `Scheduling ${this.clashDialogData.firstClashIndex} items before clash...`

        // Re-submit with scheduleUpToClash flag
        this.media.scheduleUpToClash = true
        await ScheduleApi.add(this.media)

        this.$buefy.toast.open({
          message: `Scheduled ${this.clashDialogData.firstClashIndex} item(s) before the clash. Remaining items were skipped.`,
          position: 'is-top',
          type: 'is-success',
          duration: 5000
        })

        this.loading = false
        this.$emit('refresh')
        this.closeModal()
      } catch (err) {
        console.log(err)
        if (!err || err.status === undefined || err.status === 504 || err.status === 0) {
          // Proxy timeout / no response: the up-to-clash write likely committed.
          // Don't report failure (avoids duplicate re-submits) — refresh to verify.
          this.$buefy.toast.open({
            message:
              'This is taking longer than expected to save. Refreshing to verify — please do NOT re-submit; check the grid before trying again.',
            position: 'is-top',
            type: 'is-warning',
            duration: 8000
          })
          this.$emit('refresh')
          this.closeModal()
        } else {
          this.$buefy.toast.open({
            message: 'Error scheduling items',
            position: 'is-top',
            type: 'is-danger'
          })
        }
        this.loading = false
      }
    },
    async overwriteAndSave(clashes) {
      try {
        this.showClashModal = false
        this.loading = true

        // Collect all conflicting schedule IDs
        const conflictingIds = []
        clashes.forEach(clash => {
          clash.conflicts.forEach(conflict => {
            conflictingIds.push(conflict.id)
          })
        })

        // Delete all conflicting schedules
        if (conflictingIds.length > 0) {
          await ScheduleApi.removeMultiple(conflictingIds)
        }

        // Retry the save with copy flag to bypass clash check
        this.media.copy = true
        await ScheduleApi.add(this.media)

        this.$buefy.toast.open({
          message:
            'Schedule updated successfully! Conflicting items were removed.',
          position: 'is-top',
          type: 'is-success'
        })

        this.loading = false
        this.$emit('refresh')
        this.closeModal()
      } catch (err) {
        console.log(err)
        this.$buefy.toast.open({
          message: 'Error overwriting schedule',
          position: 'is-top',
          type: 'is-danger'
        })
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.modal-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

.loading-content {
  text-align: center;
}

.loading-text {
  margin-top: 1rem;
  font-size: 1.1rem;
  color: #363636;
  font-weight: 500;
}

.auto-detected-days {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  padding: 10px;
  background-color: #f0fff4;
  border: 1px solid #48c774;
  border-radius: 4px;
}

.auto-detected-days .day-tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 5px;
}
</style>
